import { Button, Typography } from '@material-ui/core'
import { formatDate, getDifferenceDays } from 'commons/date'
import { pricePerNoti } from 'commons/number'
import {
  getPlanType,
  isPlanFreeTrialByPlanId,
  hasPlanEnded,
  isFreeTierOrFreeTrialPlanByPlanId,
  isPhysicalWebStarterPackByPlanId,
  RecurringPlanPrice,
} from 'commons/plan'
import {
  useIsPlaceRestricted,
  useIsPlaceInRechargeablePeriod,
} from 'commons/useIsPlaceRestricted'
import effects from 'app/effects/effects'
import { hasPlanCanceled } from 'commons/useSubscription'
import { isEmptyOrNil } from 'libs/empty'
import moment from 'moment'
import * as R from 'ramda'
import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import clsx from 'clsx'
import appConfigs from 'app/configs/appConfigs'
import { makeStyles } from '@material-ui/styles'
import { withRouter } from 'react-router'
import PlanBillingChangePlanDialog from './PlanBillingChangePlanDialog'
import PlanBillingChangePlanSummary from './PlanBillingChangePlanSummary'
import useChangePlan, {
  ActionType as ActionUseChangePlan,
} from './useChangePlan'
import { usePlanAddManual } from 'commons/usePlanAddManual'
import PlanPhysicalWebStarterPackInfo from './PlanPhysicalWebStarterPackInfo'
import { Skeleton } from '@material-ui/lab'

const useStyles = makeStyles((theme) => ({
  expiredIcon: {
    'background-color': theme.palette.warning,
  },
  available: {
    backgroundColor: theme.palette.available.main,
    color: theme.palette.available.intensive,
    padding: '2px 5px',
  },
  used: {
    backgroundColor: theme.palette.used.main,
    color: theme.palette.used.intensive,
    padding: '2px 5px',
  },
  buttonDisable: {
    backgroundColor: 'rgba(204, 204, 204, 1) !important',
    color: 'rgba(171, 171, 171, 1) !important',
  },
}))

export const PlanBillingYourPlan = withRouter(
  ({ planBill, shippingAddress, payment, onFetch, location, history }) => {
    const subscription = R.prop('activeSubscription', planBill)
    const balance = R.prop('balance', planBill)
    const nextSubscription = R.prop('nextSubscription', planBill)
    const [state, dispatch] = useChangePlan(
      location,
      history,
      appConfigs.enableChangePlan,
    )
    const { formatMessage } = useIntl()
    const classes = useStyles()
    const isPlaceRestricted = useIsPlaceRestricted()
    const isPlaceInRechargeablePeriod = useIsPlaceInRechargeablePeriod()
    const plan = R.prop('plan', subscription)
    const [isPlanAddManual] = usePlanAddManual()

    const handlePurchase = async () => {
      try {
        await effects.apis.purchasePlan(R.prop('changePlanFormData', state))

        if (
          isPhysicalWebStarterPackByPlanId(
            R.path(['changePlanFormData', 'planId'], state),
          )
        ) {
          if (R.path(['changePlanFormData', 'shippingAddress', 'id'], state))
            return await effects.apis.updateBillingShippingAddress(
              R.path(['changePlanFormData', 'shippingAddress'], state),
            )
          await effects.apis.createBillingShippingAddress(
            R.path(['changePlanFormData', 'shippingAddress'], state),
          )
        }
        onFetch()
      } finally {
        dispatch({ type: ActionUseChangePlan.CLOSE_CHANGE_PLAN_DIALOG })
      }
    }

    return (
      <>
        <div className="sm:flex justify-between">
          <Typography variant="h3" className={clsx('flex-1 font-bold')}>
            {R.isNil(plan) ? (
              <div className="mr-10">
                <Skeleton />
              </div>
            ) : (
              renderPlanInfo(
                formatMessage,
                subscription,
                isPlaceRestricted,
                classes,
              )
            )}
          </Typography>
          <div className="space-x-5 flex sm:justify-end py-5">
            <Button
              onClick={() => {
                dispatch({ type: ActionUseChangePlan.OPEN_CHANGE_PLAN_DIALOG })
              }}
              variant="contained"
              className={clsx(
                ' self-start',
                isPlaceInRechargeablePeriod
                  ? classes.buttonDisable
                  : 'bg-white text-blue-primary',
              )}
              disabled={isPlaceInRechargeablePeriod}
              data-testid="changePlanButton"
              style={getStyleActionButton(plan, subscription)}
            >
              {formatMessage({
                id: getActionLabel(plan, subscription, isPlanAddManual),
              })}
            </Button>
          </div>
        </div>
        <PlanBillingInformation
          subscription={subscription}
          balance={balance}
          nextSubscription={nextSubscription}
        />

        <PlanBillingChangePlanDialog
          isOpen={state.isOpenChangePlanDialog}
          paymentMethod={R.prop('paymentMethods', payment)}
          initialValue={{
            ...R.prop('addresses', payment),
            shippingAddress: shippingAddress,
          }}
          onClose={() =>
            dispatch({ type: ActionUseChangePlan.CLOSE_CHANGE_PLAN_DIALOG })
          }
          onSubmit={(data) => {
            dispatch({
              type: ActionUseChangePlan.OPEN_SUMMARY_DIALOG,
              payload: data,
            })
          }}
          planBill={planBill}
        />
        <PlanBillingChangePlanSummary
          data={R.propOr({}, 'changePlanFormData', state)}
          onCancel={() => {
            dispatch({ type: ActionUseChangePlan.CLOSE_SUMMARY_DIALOG })
            dispatch({
              type: ActionUseChangePlan.CLOSE_CHANGE_PLAN_DIALOG,
            })
          }}
          onClose={() =>
            dispatch({ type: ActionUseChangePlan.CLOSE_SUMMARY_DIALOG })
          }
          isOpen={R.prop('isOpenSummaryDialog', state)}
          onPurchase={handlePurchase}
          subscription={subscription}
        />
      </>
    )
  },
)

const PlanBillingInformation = ({
  subscription,
  balance,
  nextSubscription,
}) => {
  const plan = R.prop('plan', subscription)
  const nextPlan = nextSubscription
  const getPlanProp = R.prop(R.__, plan)
  const endsPlanAt = R.prop('endsAt', subscription)
  const beacon = R.prop('totalBeacon', balance) ?? 0

  const isPastDue = (date) => {
    return R.isNil(date) ? false : moment().isAfter(moment(date))
  }

  if (isPhysicalWebStarterPackByPlanId(getPlanProp('id'))) {
    return isEmptyOrNil(endsPlanAt) ? (
      <>
        <Typography className="my-16">
          <FormattedMessage
            id="planAndBilling.yourPlan.changePlan.recurring.label"
            values={{
              type: getPlanType(R.prop('id', plan)),
              price: RecurringPlanPrice[getPlanProp('id')],
            }}
          />
        </Typography>
        {isPastDue(R.prop('nextBillingDate', subscription)) ? (
          <Typography className="mb-16">
            <FormattedMessage
              id="planAndBilling.yourPlan.plan.info.pastDue.label"
              values={{
                date: formatDate(
                  R.propOr(moment(), 'nextBillingDate', subscription),
                ),
              }}
            />
          </Typography>
        ) : (
          <Typography className="mb-16">
            <FormattedMessage
              id="planAndBilling.yourPlan.paymentSummary.nextDue"
              values={{
                date: formatDate(
                  R.propOr(moment(), 'nextBillingDate', subscription),
                ),
              }}
            />
          </Typography>
        )}
      </>
    ) : (
      <Typography className="my-16">
        <FormattedMessage
          id="planAndBilling.yourPlan.plan.info.expiresOn.label"
          values={{
            date: formatDate(endsPlanAt),
          }}
        />
      </Typography>
    )
  }

  if (isPlanFreeTrialByPlanId(getPlanProp('id')))
    return (
      <PlanBillingFreeTrialInformation
        endsAt={endsPlanAt}
        subscription={subscription}
        trialPeriodCount={getDifferenceDays({
          start: R.prop('createdAt', subscription),
          end: endsPlanAt,
        })}
        notesQuantity={getPlanProp('notesQuantity')}
      />
    )

  return (
    <>
      <div>
        <div className="space-y-5 mt-5">
          <Typography variant="body1">
            {R.isNil(plan) ? (
              <Skeleton />
            ) : (
              <FormattedMessage
                id="planAndBilling.yourPlan.plan.info.label"
                values={{
                  numberNotifications: getPlanProp('notesQuantity'),
                  priceRatio: pricePerNoti(
                    getPlanProp('price'),
                    getPlanProp('notesQuantity'),
                  ),
                  type: getPlanType(R.prop('id', plan)),
                  b: (text) => <b>{text}</b>,
                }}
              />
            )}
          </Typography>
          <Typography variant="body1">
            {R.isNil(plan) ? (
              <Skeleton />
            ) : (
              <FormattedMessage
                id="planAndBilling.yourPlan.plan.info.price.label"
                values={{
                  price: getPlanProp('price'),
                  type: getPlanType(R.prop('id', plan)),
                }}
              />
            )}
          </Typography>
          <Typography variant="body1">
            {R.isNil(balance) ? (
              <Skeleton />
            ) : (
              <FormattedMessage
                id="planAndBilling.yourPlan.totalBeacons.count.label"
                values={{ values: beacon }}
              />
            )}
          </Typography>
        </div>
        <div className="mt-72">
          {R.isNil(plan) ? (
            <Typography variant="body1">
              <Skeleton />
            </Typography>
          ) : (
            <>
              {!hasPlanEnded({ subscription }) ? (
                <>
                  <Typography className="font-bold text-16">
                    {isEmptyOrNil(endsPlanAt) ? (
                      isPastDue(R.prop('nextBillingDate', subscription)) ? (
                        <FormattedMessage
                          id="planAndBilling.yourPlan.plan.info.pastDue.label"
                          values={{
                            date: formatDate(
                              R.propOr(
                                moment(),
                                'nextBillingDate',
                                subscription,
                              ),
                            ),
                          }}
                        />
                      ) : (
                        <FormattedMessage
                          id="planAndBilling.yourPlan.plan.info.nextDue.label"
                          values={{
                            date: formatDate(
                              R.propOr(
                                moment(),
                                'nextBillingDate',
                                subscription,
                              ),
                            ),
                          }}
                        />
                      )
                    ) : (
                      <>
                        <FormattedMessage
                          id="planAndBilling.yourPlan.plan.info.expiresOn.label"
                          values={{
                            date: formatDate(endsPlanAt),
                          }}
                        />
                      </>
                    )}
                  </Typography>
                  {!isEmptyOrNil(nextPlan) && (
                    <Typography className="font-bold m-0">
                      <FormattedMessage
                        id="planAndBilling.yourPlan.plan.downgrade.label"
                        values={{
                          nextPlan: R.path(['plan', 'name'], nextPlan),
                        }}
                      />
                    </Typography>
                  )}
                </>
              ) : (
                <FormattedMessage
                  id="planAndBilling.yourPlan.plan.info.expired.label"
                  values={{
                    date: formatDate(endsPlanAt),
                  }}
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}

const PlanBillingFreeTrialInformation = ({
  trialPeriodCount,
  endsAt = moment(),
  subscription,
  notesQuantity = 10000,
}) => (
  <div>
    <Typography className="mt-12">
      <FormattedMessage
        id="planAndBilling.yourPlan.plan.freeTrial.info.label"
        values={{
          notesQuantity,
          trialPeriodCount,
          b: (text) => <b>{text}</b>,
        }}
      />
    </Typography>
    {hasPlanEnded({ subscription }) ? (
      <Typography className="mt-12">
        <FormattedMessage id="planAndBilling.yourPlan.plan.freeTrial.ended" />
      </Typography>
    ) : (
      <Typography className="mt-12">
        <FormattedMessage
          id="planAndBilling.yourPlan.plan.info.expiresOn.label"
          values={{
            date: formatDate(endsAt),
          }}
        />
      </Typography>
    )}
  </div>
)

function getStyleActionButton(plan, subscription) {
  if (
    isPhysicalWebStarterPackByPlanId(R.prop('id', plan)) &&
    !hasPlanCanceled(subscription)
  ) {
    return { display: 'none' }
  }

  if (appConfigs.enableChangePlan) return {}

  return { display: 'none' }
}

function renderPlanInfo(
  formatMessage,
  subscription,
  isPlaceRestricted,
  classes,
) {
  const plan = R.prop('plan', subscription)
  const getPlanProp = R.prop(R.__, plan)

  if (isPhysicalWebStarterPackByPlanId(getPlanProp('id'))) {
    return (
      <>
        {getPlanProp('name')}
        <PlanPhysicalWebStarterPackInfo plan={plan} />
        {isPlaceRestricted && (
          <Typography
            className={clsx(
              classes.expiredIcon,
              'rounded-lg py-4 px-8 text-white ltr:ml-12 rtl:mr-12 inline-block',
            )}
          >
            <FormattedMessage id={'planAndBilling.yourPlan.plan.expired'} />
          </Typography>
        )}
      </>
    )
  }

  return isPlanFreeTrialByPlanId(getPlanProp('id')) ? (
    formatMessage(
      {
        id: 'planAndBilling.yourPlan.plan.freeTrial.label',
      },
      {
        trialPeriodCount: getDifferenceDays({
          start: R.prop('createdAt', subscription),
          end: R.prop('endsAt', subscription),
        }),
      },
    )
  ) : (
    <>{getPlanProp('name')}</>
  )
}

function getActionLabel(plan, subscription, isPlanAddManual) {
  const textChangePlan = 'planAndBilling.button.changePlane'
  if (isFreeTierOrFreeTrialPlanByPlanId(R.prop('id', plan))) {
    return 'action.upgrade'
  }

  if (isPlanAddManual) {
    return textChangePlan
  }

  return R.prop('endsAt', subscription) ? 'action.renew' : textChangePlan
}
